import { useEffect, useMemo, useState } from 'react';
import {
  Stack, TextInput, NumberInput, Select, Switch, Group, Button, Divider, Title, Text,
  Grid, SegmentedControl, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconMail, IconTrash, IconDeviceFloppy } from '@tabler/icons-react';
import { useVisitor, useSaveVisitor, useDeleteVisitor } from '../api/hooks/useVisitors.ts';
import type { Visitor } from '../api/schemas/visitor.ts';
import { CountrySelect } from './CountrySelect.tsx';
import { NationalitySelect } from './NationalitySelect.tsx';
import { LanguageMultiselect } from './LanguageMultiselect.tsx';
import { VisitorImage, promoteVisitorImage } from './VisitorImage.tsx';
import { api } from '../api/client.ts';
import { parseLocalDate } from '../lib/dates.ts';

/**
 * Inline visitor edit form. UI-level required-field rules mirror the
 * legacy Angular form (which is stricter than the DB nullability).
 *
 * Required (red label + asterisk in the legacy):
 *   First Name, Last Name, Address, Country, Email, Telephone Number,
 *   Type, Year Came to Gyan, Date Of Birth, Nationality, Language(s),
 *   Passport Number, Passport Valid Till, Emergency Contact Name,
 *   Emergency Relationship to You, Emergency Telephone Numbers.
 *
 * Not required: Known As, Sister/Brother, Centre, First Time to Madhuban,
 *                OCI/PIO.
 *
 * Save / Delete / Send Email are disabled while validation fails — matches
 * the legacy grey-out behaviour.
 */
const EMPTY: Visitor = {
  id: null,
  firstName: '', lastName: '',
  dateOfBirth: null, spiritualYearOfBirth: null,
  title: '', gender: '', roles: '', languages: '',
  email: '', phones: '',
  firstTime: false,
  passportNumber: '', passportValidTill: null,
  nationality: '', centre: '', address: '', knownby: '',
  emergencyName: '', emergencyRelationship: '', emergencyPhones: '',
  oci: false, inGroup: null,
  lastUpdated: null, consentRequested: null, consentReceived: null, consentExpires: null,
  occupation: '', organisation: '',
  custom1: '', custom2: '', custom3: '', custom4: '', custom5: '',
  ownerId: null, countryId: null,
};

const requiredText = (label: string) =>
  (v: unknown) => (v == null || String(v).trim() === '' ? `${label} is required` : null);

const requiredNumber = (label: string) =>
  (v: unknown) => (v == null || v === '' || Number.isNaN(Number(v)) ? `${label} is required` : null);

const requiredEmail = (label: string) =>
  (v: unknown) => {
    if (v == null || String(v).trim() === '') return `${label} is required`;
    return /\S+@\S+\.\S+/.test(String(v)) ? null : `${label} must be a valid email`;
  };

export interface VisitorFormProps {
  visitorId: number | 'new' | null;
  ownerRoleId: number | null;
  onSaved?: (saved: Visitor) => void;
  onDeleted?: (id: number) => void;
}

export function VisitorForm({ visitorId, ownerRoleId, onSaved, onDeleted }: VisitorFormProps) {
  const numericId = typeof visitorId === 'number' ? visitorId : null;
  const isNew = visitorId === 'new';
  const loaded = useVisitor(numericId);
  const save = useSaveVisitor();
  const remove = useDeleteVisitor();

  const [pendingImageTempId, setPendingImageTempId] = useState<string | null>(null);

  const form = useForm<Visitor>({
    initialValues: { ...EMPTY, ownerId: ownerRoleId },
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: {
      firstName: requiredText('First Name'),
      lastName: requiredText('Last Name'),
      address: requiredText('Address'),
      countryId: (v: unknown) => (v == null ? 'Country is required' : null),
      email: requiredEmail('Email'),
      phones: requiredText('Telephone Number'),
      roles: requiredText('Type'),
      spiritualYearOfBirth: requiredNumber('Year Came to Gyan'),
      dateOfBirth: (v: unknown) => (v == null || v === '' ? 'Date Of Birth is required' : null),
      nationality: requiredText('Nationality'),
      languages: requiredText('Language(s)'),
      passportNumber: requiredText('Passport Number'),
      passportValidTill: (v: unknown) => (v == null || v === '' ? 'Passport Valid Till is required' : null),
      emergencyName: requiredText('Emergency Contact Name'),
      emergencyRelationship: requiredText('Emergency Relationship to You'),
      emergencyPhones: requiredText('Emergency Telephone Numbers'),
    },
  });

  useEffect(() => {
    if (loaded.data) {
      form.setValues({ ...EMPTY, ...loaded.data });
      setPendingImageTempId(null);
    } else if (isNew) {
      form.setValues({ ...EMPTY, ownerId: ownerRoleId });
      setPendingImageTempId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded.data?.id, isNew]);

  // Re-evaluate validity every render (form.errors is updated by validateInputOnChange).
  const isInvalid = useMemo(() => Object.keys(form.errors).length > 0, [form.errors]);

  if (visitorId == null) {
    return (
      <Stack align="center" justify="center" h="100%" gap="xs" mih={300}>
        <Text c="dimmed">Select a visitor on the left, or add a new one.</Text>
      </Stack>
    );
  }

  async function submit(values: Visitor) {
    try {
      const saved = await save.mutateAsync(values);
      let imageOk = true;
      if (saved.id != null && pendingImageTempId) {
        imageOk = await promoteVisitorImage(saved.id, pendingImageTempId);
        setPendingImageTempId(null);
      }
      notifications.show({ message: 'Visitor saved', color: 'green' });
      if (!imageOk) {
        notifications.show({ message: 'Visitor saved, but the photo could not be updated.', color: 'yellow' });
      }
      onSaved?.(saved);
    } catch (err) {
      notifications.show({
        message: 'Save failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    }
  }

  function onDelete() {
    if (numericId == null) return;
    modals.openConfirmModal({
      title: 'Delete visitor',
      children: <Text size="sm">Deletes the visitor and all their travels. Continue?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        remove.mutate(numericId, {
          onSuccess: () => {
            notifications.show({ message: 'Deleted', color: 'green' });
            onDeleted?.(numericId);
          },
          onError: err => notifications.show({
            message: 'Delete failed' + (err instanceof Error ? `: ${err.message}` : ''),
            color: 'red',
          }),
        });
      },
    });
  }

  async function sendEmail() {
    if (numericId == null) return;
    try {
      const res = await api.get(`/api/visitors-email/${numericId}`);
      if (res.status === 200) {
        notifications.show({ message: 'Email sent to your account', color: 'green' });
      } else {
        notifications.show({ message: `Send failed: ${res.status}`, color: 'red' });
      }
    } catch (err) {
      notifications.show({
        message: 'Send failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    }
  }

  const fullName = `${form.values.firstName ?? ''} ${form.values.lastName ?? ''}`.trim();

  const inputSize = 'xs';

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack gap="xs">
        <Title order={4}>{isNew ? 'New Traveller' : (fullName || `#${numericId}`)}</Title>

        {/*
          Inner photo/Country split is tied to the SMALL viewport breakpoint
          (~768px), not the page's md (~992px) where travel reflows. The form
          is wide enough to keep the 8/4 split even when the page has
          collapsed back to a two-column layout — only true phones should
          stack the photo below the form fields.
        */}
        <Grid gutter="xs">
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Stack gap="xs">
              <Grid gutter="xs">
                <Grid.Col span={6}>
                  <TextInput size={inputSize} label="First Name" withAsterisk {...form.getInputProps('firstName')} />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput size={inputSize} label="Last Name" withAsterisk {...form.getInputProps('lastName')} />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput size={inputSize} label="Known As" {...form.getInputProps('knownby')} />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={2}>
                    <Text size="xs" component="label">Sister / Brother</Text>
                    <SegmentedControl
                      size="xs"
                      data={[
                        { value: 'Sister', label: 'Sister' },
                        { value: 'Brother', label: 'Brother' },
                      ]}
                      value={form.values.gender ?? ''}
                      onChange={v => form.setFieldValue('gender', v)}
                      fullWidth
                    />
                  </Stack>
                </Grid.Col>
              </Grid>
              <Textarea
                size={inputSize}
                label="Address"
                withAsterisk
                autosize
                minRows={2}
                maxRows={3}
                {...form.getInputProps('address')}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Stack gap="xs">
              <VisitorImage
                visitorId={numericId}
                editable
                pendingTempId={pendingImageTempId}
                onTempUploaded={setPendingImageTempId}
              />
              <CountrySelect
                label="Country *"
                value={form.values.countryId ?? null}
                onChange={id => form.setFieldValue('countryId', id)}
              />
              {form.errors.countryId && (
                <Text size="xs" c="red">{String(form.errors.countryId)}</Text>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider my={2} />

        <Grid gutter="xs">
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput size={inputSize} label="Email" withAsterisk type="email" {...form.getInputProps('email')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput size={inputSize} label="Telephone Number" withAsterisk {...form.getInputProps('phones')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <TextInput size={inputSize} label="Centre" {...form.getInputProps('centre')} />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Select
              size={inputSize}
              label="Type"
              withAsterisk
              data={['BK', 'Student', 'Family', 'Guest', 'Staff']}
              value={form.values.roles ?? null}
              onChange={v => form.setFieldValue('roles', v ?? '')}
              error={form.errors.roles as string | undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <NumberInput
              size={inputSize}
              label="Year Came to Gyan"
              withAsterisk
              value={form.values.spiritualYearOfBirth ?? ''}
              onChange={v => form.setFieldValue('spiritualYearOfBirth', typeof v === 'number' ? v : null)}
              error={form.errors.spiritualYearOfBirth as string | undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Switch
              size="sm"
              label="First Time to Madhuban"
              checked={Boolean(form.values.firstTime)}
              onChange={e => form.setFieldValue('firstTime', e.currentTarget.checked)}
              mt={22}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <DatePickerInput
              size={inputSize}
              label="Date Of Birth"
              withAsterisk
              value={parseLocalDate(form.values.dateOfBirth)}
              onChange={d => form.setFieldValue('dateOfBirth', d ? toIso(d as Date) : null)}
              valueFormat="DD/MM/YYYY"
              error={form.errors.dateOfBirth as string | undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {/*
              Nationality is a STRING field (country name), separate from
              `countryId` above. They draw from the same country list but
              are stored independently — changing one must not move the other.
            */}
            <NationalitySelect
              label="Nationality *"
              value={form.values.nationality ?? null}
              onChange={v => form.setFieldValue('nationality', v ?? '')}
            />
            {form.errors.nationality && (
              <Text size="xs" c="red">{String(form.errors.nationality)}</Text>
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <LanguageMultiselect
              label="Language(s) *"
              value={form.values.languages ?? ''}
              onChange={v => form.setFieldValue('languages', v)}
            />
            {form.errors.languages && (
              <Text size="xs" c="red">{String(form.errors.languages)}</Text>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput size={inputSize} label="Passport Number" withAsterisk {...form.getInputProps('passportNumber')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <DatePickerInput
              size={inputSize}
              label="Passport Valid Till"
              withAsterisk
              value={parseLocalDate(form.values.passportValidTill)}
              onChange={d => form.setFieldValue('passportValidTill', d ? toIso(d as Date) : null)}
              valueFormat="DD/MM/YYYY"
              error={form.errors.passportValidTill as string | undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Switch
              size="sm"
              label="OCI/PIO"
              checked={Boolean(form.values.oci)}
              onChange={e => form.setFieldValue('oci', e.currentTarget.checked)}
              mt={22}
            />
          </Grid.Col>
        </Grid>

        <Divider my={2} />

        <Grid gutter="xs">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput size={inputSize} label="Emergency Contact Name" withAsterisk {...form.getInputProps('emergencyName')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput size={inputSize} label="Emergency Relationship to You" withAsterisk {...form.getInputProps('emergencyRelationship')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput size={inputSize} label="Emergency Telephone Numbers" withAsterisk {...form.getInputProps('emergencyPhones')} />
          </Grid.Col>
        </Grid>

        <Group gap="xs" mt="xs">
          <Button
            type="submit"
            color="blue"
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            loading={save.isPending}
            disabled={isInvalid}
          >
            Save
          </Button>
          <Button
            type="button"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={onDelete}
            disabled={isNew}
          >
            Delete
          </Button>
          <Button
            type="button"
            variant="default"
            size="xs"
            leftSection={<IconMail size={14} />}
            onClick={sendEmail}
            disabled={isNew}
          >
            Send Email
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
